<template>
  <div v-if="enabled" class="glad-ai-root">
    <!-- Floating action button -->
    <button
      v-if="!open"
      type="button"
      class="glad-ai-fab"
      aria-label="Open GLAD® AI assistant"
      @click="openChat">
      <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
        <path fill="currentColor" d="M12 3a9 9 0 0 0-8.4 12.27L2.5 20.5a1 1 0 0 0 1.21 1.27l4.81-1.45A9 9 0 1 0 12 3Zm-3.5 8.25a1.25 1.25 0 1 1 0 2.5 1.25 1.25 0 0 1 0-2.5Zm3.5 0a1.25 1.25 0 1 1 0 2.5 1.25 1.25 0 0 1 0-2.5Zm3.5 0a1.25 1.25 0 1 1 0 2.5 1.25 1.25 0 0 1 0-2.5Z"/>
      </svg>
      <span>Ask GLAD&reg; AI</span>
    </button>

    <!-- Drawer -->
    <div v-else class="glad-ai-drawer" role="dialog" aria-label="GLAD AI assistant">
      <header class="glad-ai-drawer__head">
        <div>
          <p class="glad-ai-drawer__eyebrow">Project GLAD&reg; · AI assistant</p>
          <h2 class="glad-ai-drawer__title">How can I help?</h2>
        </div>
        <button
          type="button"
          class="glad-ai-drawer__close"
          aria-label="Close assistant"
          @click="open = false">
          <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
            <path stroke="currentColor" stroke-width="2" stroke-linecap="round" d="M4 4l12 12M16 4L4 16"/>
          </svg>
        </button>
      </header>

      <!-- Message list -->
      <div ref="scrollEl" class="glad-ai-messages">
        <div v-if="messages.length === 0" class="glad-ai-seed">
          <p>Hi — I can help you navigate the platform. Try:</p>
          <ul>
            <li v-for="(p, i) in promptSuggestions" :key="i">
              <button type="button" class="glad-ai-seed__chip" @click="quickAsk(p)">{{ p }}</button>
            </li>
          </ul>
        </div>

        <div
          v-for="(m, i) in messages"
          :key="'m'+i"
          class="glad-ai-msg"
          :class="'glad-ai-msg--' + m.role">

          <div v-if="m.role === 'user'" class="glad-ai-msg__bubble">{{ m.text }}</div>

          <template v-else>
            <div v-if="m.tool_calls && m.tool_calls.length" class="glad-ai-tools">
              <span
                v-for="(t, ti) in m.tool_calls"
                :key="ti"
                class="glad-ai-tool-pill"
                :title="JSON.stringify(t.args || {})">
                <svg viewBox="0 0 16 16" width="11" height="11" aria-hidden="true">
                  <path fill="currentColor" d="M14 7H8.4L11 4.4 9.6 3 4.6 8l5 5L11 11.6 8.4 9H14z"/>
                </svg>
                {{ toolLabel(t.name) }}
              </span>
            </div>
            <div class="glad-ai-msg__bubble" v-html="renderText(m.text)"></div>
          </template>
        </div>

        <!-- Streaming-in-progress placeholder -->
        <div v-if="streaming" class="glad-ai-msg glad-ai-msg--model">
          <div v-if="liveToolCalls.length" class="glad-ai-tools">
            <span v-for="(t, ti) in liveToolCalls" :key="'live'+ti" class="glad-ai-tool-pill">
              <span class="glad-ai-tool-pill__dot"></span>
              {{ toolLabel(t.name) }}
            </span>
          </div>
          <div class="glad-ai-msg__bubble" v-html="renderText(liveText) || '&nbsp;'"></div>
        </div>

        <p v-if="error" class="glad-ai-error" role="alert">{{ error }}</p>
      </div>

      <!-- Composer -->
      <form class="glad-ai-composer" @submit.prevent="send">
        <textarea
          v-model="draft"
          class="glad-ai-input"
          rows="2"
          maxlength="5000"
          :disabled="streaming"
          placeholder="Ask anything about the platform…"
          @keydown.meta.enter.prevent="send"
          @keydown.ctrl.enter.prevent="send"></textarea>
        <div class="glad-ai-composer__row">
          <button
            v-if="messages.length"
            type="button"
            class="glad-ai-btn glad-ai-btn--ghost"
            :disabled="streaming"
            @click="resetChat">
            New chat
          </button>
          <span class="glad-ai-hint">⌘ / Ctrl + Enter</span>
          <button
            type="submit"
            class="glad-ai-btn"
            :disabled="streaming || !canSubmit">
            {{ streaming ? 'Thinking…' : 'Send' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AiSupportChat',
  props: {
    /** Force-disable in environments where you don't want a chat icon */
    enabled: { type: Boolean, default: true },
  },
  data() {
    return {
      open: false,
      draft: '',
      streaming: false,
      error: '',
      // Full conversation kept locally — sent back each turn for multi-turn memory.
      messages: [], // [{ role: 'user'|'model', text, tool_calls? }]
      // Live streaming buffers
      liveText: '',
      liveToolCalls: [],
      eventSource: null,
      abortRef: null,
      promptSuggestions: [
        'What pathways am I enrolled in?',
        'How do I earn the next badge?',
        'What\'s in the Content Library?',
        'Walk me through enrolling in a pathway.',
      ],
    };
  },
  computed: {
    canSubmit() { return this.draft.trim().length > 0; },
  },
  beforeDestroy() {
    this.closeStream();
  },
  methods: {
    openChat() {
      this.open = true;
      this.$nextTick(() => this.scrollToBottom());
    },
    resetChat() {
      this.closeStream();
      this.messages = [];
      this.error = '';
      this.liveText = '';
      this.liveToolCalls = [];
    },
    quickAsk(prompt) {
      this.draft = prompt;
      this.send();
    },
    send() {
      if (!this.canSubmit || this.streaming) return;
      const text = this.draft.trim();
      this.draft = '';
      this.error = '';
      this.messages.push({ role: 'user', text });
      this.startStream();
    },
    startStream() {
      this.streaming = true;
      this.liveText = '';
      this.liveToolCalls = [];

      const url = this.apiUrl('glad/ai/ask-stream');
      const token = this.token();
      if (!token) {
        this.error = 'You need to sign in again before using the assistant.';
        this.streaming = false;
        return;
      }

      // EventSource doesn't support POST bodies, so we open a fetch with
      // streaming reader. This lets us POST the conversation while still
      // consuming SSE.
      const ctrl = new AbortController();
      this.abortRef = ctrl;

      fetch(url, {
        method: 'POST',
        signal: ctrl.signal,
        headers: {
          'Accept':        'text/event-stream',
          'Content-Type':  'application/json',
          'Authorization': 'Bearer ' + token,
        },
        body: JSON.stringify({
          messages: this.messages.map(m => ({ role: m.role, text: m.text })),
          context_path: this.$route ? this.$route.fullPath : null,
          gladRole: (typeof localStorage !== 'undefined') ? (localStorage.getItem('hot-user') || '') : '',
        }),
      })
        .then(resp => {
          if (!resp.ok) throw new Error('HTTP ' + resp.status);
          return this.consumeSse(resp);
        })
        .catch(err => {
          if (err && err.name === 'AbortError') return; // user closed the chat
          this.error = 'The assistant couldn\'t respond. ' + (err.message || '');
        })
        .finally(() => {
          // Commit the streamed assistant message into the persistent history.
          if (this.liveText.trim() || this.liveToolCalls.length) {
            this.messages.push({
              role: 'model',
              text: this.liveText,
              tool_calls: this.liveToolCalls.slice(),
            });
          }
          this.liveText = '';
          this.liveToolCalls = [];
          this.streaming = false;
          this.abortRef = null;
          this.$nextTick(() => this.scrollToBottom());
        });
    },
    async consumeSse(response) {
      const reader  = response.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        // SSE frames are separated by blank lines
        let idx;
        while ((idx = buffer.indexOf('\n\n')) !== -1) {
          const frame = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);
          this.handleSseFrame(frame);
        }
      }
    },
    handleSseFrame(frame) {
      let event = 'message';
      let data  = '';
      frame.split('\n').forEach(line => {
        if (line.startsWith('event: ')) event = line.slice(7).trim();
        else if (line.startsWith('data: ')) data += line.slice(6);
      });
      if (!data) return;
      let parsed;
      try { parsed = JSON.parse(data); } catch (e) { return; }

      if (event === 'text') {
        this.liveText += parsed.delta || '';
        this.scrollToBottom();
      } else if (event === 'tool_call') {
        this.liveToolCalls.push({ name: parsed.name, args: parsed.args || {} });
      } else if (event === 'tool_result') {
        // Mark the most recent tool-call entry as resolved (drop the pulsing dot)
        const last = this.liveToolCalls[this.liveToolCalls.length - 1];
        if (last) last.done = true;
      } else if (event === 'error') {
        this.error = parsed.message || 'AI service error.';
      } else if (event === 'done') {
        // No-op — finally{} block commits the message.
      }
    },
    closeStream() {
      if (this.abortRef) this.abortRef.abort();
      this.abortRef = null;
      this.streaming = false;
    },
    apiUrl(path) {
      const base = (process.env.VUE_APP_API_URL || 'http://localhost:8000/').replace(/\/$/, '');
      return base + '/' + path.replace(/^\//, '');
    },
    token() {
      return (typeof localStorage !== 'undefined') ? localStorage.getItem('hot-token') : null;
    },
    toolLabel(name) {
      return ({
        list_pathways:        'looking up pathways',
        list_my_badges:       'checking badges',
        list_content_library: 'searching content library',
        get_progress:         'checking your progress',
      })[name] || name;
    },
    renderText(text) {
      if (!text) return '';
      // Light formatter: escape HTML, then convert **bold**, *italic*, and
      // single newlines → <br>. Keeps the output readable without bringing
      // in a markdown library.
      const esc = String(text)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
      return esc
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/(^|[\s(])\*([^\*\n]+?)\*(?=[\s.,)!?]|$)/g, '$1<em>$2</em>')
        .replace(/`([^`\n]+?)`/g, '<code>$1</code>')
        .replace(/\n/g, '<br>');
    },
    scrollToBottom() {
      const el = this.$refs.scrollEl;
      if (el) el.scrollTop = el.scrollHeight;
    },
  },
};
</script>

<style scoped lang="scss">
@import '~@/assets/sass/glad-tokens.scss';

.glad-ai-root { font-family: var(--glad-font-sans); }

/* FAB ---------------------------------------------------------------------- */
/* Positioned bottom-left so it doesn't collide with the right-anchored
   language switcher. Default below assumes the sidebar is off-canvas
   (mobile + tablet); on wide screens the sidebar takes ~61px and the
   media query below shifts the FAB past it. */
.glad-ai-fab {
  position: fixed;
  /* Bottom-right, sitting just above the language translator pill (fixed at
     right:1.25rem / bottom:1.25rem, ~34px tall). The translator widget uses a
     huge z-index (2e9), so we both clear it vertically AND sit above it so the
     button can never be hidden behind it. */
  bottom: 5.25rem;
  right: 1.25rem;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.7rem 1rem;
  background: var(--glad-ink);
  color: #fff;
  border: 0;
  border-radius: 999px;
  cursor: pointer;
  box-shadow: 0 10px 30px rgba(15, 17, 21, 0.18);
  font-family: var(--glad-font-sans);
  font-size: 0.85rem;
  font-weight: 600;
  /* Above the translator widget (z-index 2e9) so it never hides behind it. */
  z-index: 2000000001;
  transition: transform 0.12s ease, box-shadow 0.12s ease;

  &:hover { transform: translateY(-1px); box-shadow: 0 14px 36px rgba(15, 17, 21, 0.22); }
}

/* Drawer ------------------------------------------------------------------- */
.glad-ai-drawer {
  position: fixed;
  bottom: 1.25rem;
  right: 1.25rem;
  width: 380px;
  max-width: calc(100vw - 2rem);
  height: 580px;
  max-height: calc(100vh - 4rem);
  display: flex;
  flex-direction: column;
  background: var(--glad-surface);
  border: 1px solid var(--glad-line);
  border-radius: var(--glad-radius-lg);
  box-shadow: 0 20px 60px rgba(15, 17, 21, 0.22);
  overflow: hidden;
  /* Above the translator widget (z-index 2e9) so the open panel isn't pierced. */
  z-index: 2000000001;
}

.glad-ai-drawer__head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.85rem;
  padding: 0.95rem 1rem;
  background: var(--glad-ink);
  color: #fff;
}
.glad-ai-drawer__eyebrow {
  font-size: 0.66rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(255,255,255,0.65);
  margin: 0 0 0.15rem;
}
.glad-ai-drawer__title {
  font-family: var(--glad-font-serif);
  font-size: 1.05rem;
  font-weight: 700;
  margin: 0;
  line-height: 1.2;
  color: #fff;
}
.glad-ai-drawer__close {
  background: rgba(255,255,255,0.1);
  border: 0;
  border-radius: 6px;
  color: #fff;
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  &:hover { background: rgba(255,255,255,0.2); }
}

/* Messages ---------------------------------------------------------------- */
.glad-ai-messages {
  flex: 1;
  padding: 1rem;
  overflow-y: auto;
  background: var(--glad-paper);
}

.glad-ai-seed {
  font-size: 0.84rem;
  color: var(--glad-text-soft);
  p { margin: 0 0 0.6rem; }
  ul { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 0.4rem; }
}
.glad-ai-seed__chip {
  width: 100%;
  text-align: left;
  background: var(--glad-surface);
  border: 1px solid var(--glad-line);
  border-radius: var(--glad-radius);
  padding: 0.55rem 0.7rem;
  font-family: var(--glad-font-sans);
  font-size: 0.8rem;
  color: var(--glad-ink);
  cursor: pointer;
  &:hover { border-color: var(--glad-ink); }
}

.glad-ai-msg {
  display: flex;
  margin-bottom: 0.85rem;
  flex-direction: column;
  &--user { align-items: flex-end; }
  &--model { align-items: flex-start; }
}
.glad-ai-msg__bubble {
  max-width: 86%;
  padding: 0.6rem 0.8rem;
  border-radius: 12px;
  font-size: 0.86rem;
  line-height: 1.5;
  white-space: pre-wrap;
  word-wrap: break-word;
  ::v-deep code {
    font-family: ui-monospace, 'SF Mono', Menlo, monospace;
    background: rgba(15, 17, 21, 0.06);
    padding: 0 0.25rem;
    border-radius: 3px;
    font-size: 0.78rem;
  }
}
.glad-ai-msg--user  .glad-ai-msg__bubble { background: var(--glad-ink);     color: #fff; }
.glad-ai-msg--model .glad-ai-msg__bubble { background: var(--glad-surface); color: var(--glad-ink); border: 1px solid var(--glad-line); }

.glad-ai-tools {
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
  margin-bottom: 0.4rem;
}
.glad-ai-tool-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.7rem;
  font-weight: 600;
  padding: 0.18rem 0.5rem;
  background: var(--glad-paper-2);
  color: var(--glad-text-soft);
  border-radius: 999px;
}
.glad-ai-tool-pill__dot {
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--glad-accent);
  animation: glad-ai-pulse 1s ease-in-out infinite;
}
@keyframes glad-ai-pulse { 0%, 100% { opacity: 0.4; } 50% { opacity: 1; } }

.glad-ai-error {
  margin: 0.5rem 0;
  font-size: 0.78rem;
  color: var(--glad-danger);
}

/* Composer ---------------------------------------------------------------- */
.glad-ai-composer {
  padding: 0.65rem 0.7rem 0.7rem;
  background: var(--glad-surface);
  border-top: 1px solid var(--glad-line);
}
.glad-ai-input {
  display: block;
  width: 100%;
  border: 0;
  outline: none;
  resize: none;
  font-family: var(--glad-font-sans);
  font-size: 0.85rem;
  line-height: 1.4;
  color: var(--glad-ink);
  background: transparent;
  &::placeholder { color: var(--glad-text-muted); }
}
.glad-ai-composer__row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.55rem;
  padding-top: 0.5rem;
  border-top: 1px dashed var(--glad-line);
  margin-top: 0.4rem;
}
.glad-ai-hint {
  font-size: 0.7rem;
  color: var(--glad-text-muted);
}
.glad-ai-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.4rem 0.9rem;
  background: var(--glad-ink);
  color: #fff;
  border: 0;
  border-radius: var(--glad-radius);
  font-family: var(--glad-font-sans);
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.12s ease;
  &:hover { opacity: 0.85; }
  &:disabled { opacity: 0.45; cursor: not-allowed; }
  &--ghost {
    background: transparent;
    color: var(--glad-text-soft);
    border: 1px solid var(--glad-line);
    &:hover { background: var(--glad-paper-2); opacity: 1; }
  }
}

@media (max-width: 480px) {
  .glad-ai-fab span { display: none; }
  .glad-ai-fab     { padding: 0.7rem; }
  .glad-ai-drawer  { right: 1rem; width: auto; }
}
</style>
